import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";
import { FiRefreshCcw } from "react-icons/fi";

const today = () => new Date().toISOString().split("T")[0];

const nDaysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().split("T")[0];
};

/* ── Skeleton ── */
const SkeletonRow = () => (
    <tr className="animate-pulse">
        {[40, 160, 140, 120, 100, 120, 90, 160, 160, 110, 160, 160, 100].map((w, i) => (
            <td key={i} className="border border-[#E5E7EB] px-3 py-3.5" style={{ minWidth: w }}>
                <div className="h-3 bg-gray-200 rounded-full" style={{ width: "75%" }} />
            </td>
        ))}
    </tr>
);

/* ── Status badge ── */
const StatusBadge = ({ status }) => {
    const s = String(status ?? "").toUpperCase();
    const cls =
        s === "ACTIVE"   ? "bg-green-100 text-green-700 border-green-200" :
        s === "INACTIVE" ? "bg-red-100 text-red-700 border-red-200"       :
        s === "DELETED"  ? "bg-gray-200 text-gray-500 border-gray-300"    :
                           "bg-amber-100 text-amber-700 border-amber-200";
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${cls}`}>
            {status ?? "-"}
        </span>
    );
};

/* ── Confirm Activate Modal ── */
const ActivateModal = ({ user, onConfirm, onClose, loading }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] px-5 py-4 flex items-center justify-between">
                <h3 className="text-white font-bold text-[15px]">Activate User</h3>
                <button onClick={onClose} disabled={loading} className="text-white/70 hover:text-white text-xl leading-none disabled:opacity-40">✕</button>
            </div>
            <div className="px-5 py-5 space-y-4">
                <p className="text-sm text-gray-600">
                    Are you sure you want to activate <span className="font-semibold text-gray-800">{user?.fullName ?? user?.username}</span>?
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700 space-y-1">
                    <div><span className="font-semibold">Username:</span> {user?.username ?? "-"}</div>
                    <div><span className="font-semibold">Role:</span> {user?.role ?? "-"}</div>
                    <div><span className="font-semibold">Current Status:</span> {user?.status ?? "-"}</div>
                </div>
                <p className="text-xs text-gray-400">The user will be able to call APIs again within ~60 seconds after activation.</p>
                <div className="flex gap-3">
                    <button onClick={onClose} disabled={loading}
                        className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 disabled:opacity-50 transition">
                        Cancel
                    </button>
                    <button onClick={onConfirm} disabled={loading}
                        className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm disabled:opacity-50 transition flex items-center justify-center gap-2">
                        {loading ? (
                            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>Activating...</>
                        ) : "Activate"}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

/* ── Toast ── */
const Toast = ({ toast }) => {
    if (!toast) return null;
    const isSuccess = toast.type === "success";
    return (
        <div className={`fixed bottom-6 right-6 z-[60] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-lg text-sm font-medium ${isSuccess ? "bg-green-600 text-white" : "bg-red-600 text-white"}`}>
            {isSuccess
                ? <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                : <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
            }
            {toast.message}
        </div>
    );
};

/* ══════════════════════════════════════════════════════════ */
const DormantUser = () => {

    const [from, setFrom]               = useState(nDaysAgo(30));
    const [to, setTo]                   = useState(today());
    const [currentOnly, setCurrentOnly] = useState(true);

    const [users, setUsers]             = useState([]);
    const [count, setCount]             = useState(0);
    const [loading, setLoading]         = useState(false);

    const [activateModal, setActivateModal] = useState(null);   // user object | null
    const [activateLoading, setActivateLoading] = useState(false);

    const [toast, setToast]             = useState(null);

    /* ── API hooks ── */
    const { callApi: fetchDormant }  = useApi("/admin/user-status/dormant", "GET");
    const { callApi: activateUser }  = useApi("/admin/user-status/activate", "PUT");

    /* ── Helpers ── */
    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── Fetch list ── */
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { currentOnly };
            if (from) params.from = from;
            if (to)   params.to   = to;
            const res = await fetchDormant(null, params);
            if (res && Array.isArray(res.users)) {
                setUsers(res.users);
                setCount(res.count ?? res.users.length);
            } else if (Array.isArray(res)) {
                setUsers(res);
                setCount(res.length);
            } else {
                setUsers([]);
                setCount(0);
            }
        } catch (err) {
            showToast("error", err?.message || "Failed to fetch dormant users");
            setUsers([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

    /* ── Activate ── */
    const handleActivate = async () => {
        if (!activateModal?.userId) return;
        setActivateLoading(true);
        try {
            const res = await activateUser(null, { userId: activateModal.userId });
            showToast("success", res?.message || "User activated successfully");
            setActivateModal(null);
            fetchData();
        } catch (err) {
            showToast("error", err?.message || "Failed to activate user");
        } finally {
            setActivateLoading(false);
        }
    };

    /* ── Styles ── */
    const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2 text-left text-[11px] font-semibold text-[#0A2A4F] uppercase tracking-wide whitespace-nowrap select-none";
    const TD = "border border-[#E5E7EB] px-3 py-2.5 text-[13px] text-gray-700 align-middle";

    /* ════════════════════════ RENDER ════════════════════════ */
    return (
        <div className="h-screen flex flex-col overflow-hidden bg-[#F0F4FA]" style={{ fontFamily: "'Inter','ui-sans-serif',system-ui,sans-serif" }}>

            {/* ══ HEADER ══ */}
            <div className="flex-shrink-0 z-20 shadow-sm">

                {/* Title bar */}
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3.5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-[#0A2A4F] flex items-center justify-center flex-shrink-0">
                            <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <line x1="17" y1="8" x2="23" y2="14" />
                                <line x1="23" y1="8" x2="17" y2="14" />
                            </svg>
                        </div>
                        <div>
                            <div className="flex items-center gap-2.5 flex-wrap">
                                <h1 className="text-[20px] font-normal text-[#0A2A4F] leading-tight">Dormant Users</h1>
                                {count > 0 && (
                                    <span className="px-3 py-1 bg-red-50 border border-red-200 text-red-700 rounded-full text-[13px] font-medium whitespace-nowrap">
                                        {count.toLocaleString("en-IN")} {currentOnly ? "inactive" : "total"}
                                    </span>
                                )}
                            </div>
                            <p className="text-[12px] text-gray-400 mt-0.5">
                                Merchants auto-inactivated after 15 days of no transactions
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {loading && <div className="w-4 h-4 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mr-1" />}
                        <button onClick={fetchData} disabled={loading}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            <FiRefreshCcw size={13} className={loading ? "animate-spin" : ""} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Filter bar */}
                <div className="bg-white border-b border-[#E5E7EB] px-4 py-3 flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-[13px] text-gray-500 font-medium whitespace-nowrap">From</span>
                        <input
                            type="date"
                            value={from}
                            max={to || today()}
                            onChange={(e) => setFrom(e.target.value)}
                            className="h-[38px] px-3 border border-[#E5E7EB] rounded-lg bg-white text-[13px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[13px] text-gray-500 font-medium whitespace-nowrap">To</span>
                        <input
                            type="date"
                            value={to}
                            min={from}
                            max={today()}
                            onChange={(e) => setTo(e.target.value)}
                            className="h-[38px] px-3 border border-[#E5E7EB] rounded-lg bg-white text-[13px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E7EB] bg-white cursor-pointer select-none"
                        onClick={() => setCurrentOnly(!currentOnly)}>
                        <div className={`w-9 h-5 rounded-full transition-colors ${currentOnly ? "bg-[#0A2A4F]" : "bg-gray-300"} relative flex-shrink-0`}>
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${currentOnly ? "translate-x-4" : "translate-x-0.5"}`} />
                        </div>
                        <span className="text-[13px] text-gray-600 font-medium whitespace-nowrap">Still Inactive Only</span>
                    </div>
                    <button onClick={fetchData} disabled={loading}
                        className="h-[38px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[13px] transition flex items-center gap-2">
                        <FiRefreshCcw size={13} className={loading ? "animate-spin" : ""} />
                        Apply
                    </button>
                </div>

                {/* Summary strip */}
                <div className="bg-white border-b border-[#E5E7EB] px-4 flex items-stretch divide-x divide-[#E5E7EB] overflow-x-auto">
                    <div className="flex items-center gap-2.5 px-5 py-2.5 bg-[#FFF5F5] select-none flex-shrink-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0 bg-red-500" />
                        <span className="text-[12px] font-medium text-gray-400">Dormant Users</span>
                        <span className="text-[17px] font-normal tabular-nums text-red-700">{count.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="flex items-center gap-2 px-5 py-2.5 select-none flex-shrink-0">
                        <span className="text-[12px] text-gray-400">Filter</span>
                        <span className="text-[12px] font-semibold text-[#0A2A4F]">
                            {from && to ? `${from} → ${to}` : from ? `From ${from}` : to ? `Until ${to}` : "All time"}
                        </span>
                        {!currentOnly && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[11px] rounded border border-amber-200">Including Reactivated</span>}
                    </div>
                </div>
            </div>

            {/* ══ TABLE ══ */}
            <div className="flex-1 overflow-hidden flex flex-col px-3 pt-3 pb-16">
                <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col flex-1 overflow-hidden">

                    {/* Mobile cards */}
                    <div className="block md:hidden flex-1 overflow-y-auto divide-y divide-gray-100">
                        {loading ? (
                            <div className="py-16 flex justify-center">
                                <div className="w-8 h-8 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : users.length > 0 ? (
                            users.map((u, i) => (
                                <div key={i} className="px-4 py-4 space-y-3">
                                    <div className="flex justify-between items-start gap-2">
                                        <div>
                                            <p className="text-[13px] font-semibold text-[#0A2A4F]">{u.fullName ?? u.username ?? "-"}</p>
                                            {u.shopName && <p className="text-[12px] text-gray-700 font-medium mt-0.5">{u.shopName}</p>}
                                            <p className="text-[11px] text-gray-500 font-mono mt-0.5">{u.bpUserId ?? u.username ?? "-"}</p>
                                            <p className="text-[12px] text-gray-600">{u.mobileNumber ?? "-"}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1">
                                            <StatusBadge status={u.status} />
                                            <span className="text-[11px] text-gray-400">{u.role ?? ""}</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                                        <div><span className="text-gray-400">Inactivated:</span> <span className="text-gray-700 font-medium">{u.inactivatedAt ?? "-"}</span></div>
                                        <div><span className="text-gray-400">Last Txn:</span> <span className="text-gray-700 font-medium">{u.lastTxnAt ?? "Never"}</span></div>
                                        <div><span className="text-gray-400">Days Idle:</span> <span className="font-semibold text-red-600">{u.daysSinceLastTxn != null ? u.daysSinceLastTxn : "-"}</span></div>
                                        {u.reactivatedAt && <div><span className="text-gray-400">Reactivated:</span> <span className="text-green-700 font-medium">{u.reactivatedAt}</span></div>}
                                    </div>
                                    {u.status !== "ACTIVE" && (
                                        <button onClick={() => setActivateModal(u)}
                                            className="w-full py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-[13px] transition">
                                            Activate
                                        </button>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="py-20 flex flex-col items-center gap-4 text-center px-4">
                                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">😴</div>
                                <div>
                                    <p className="text-sm font-medium text-gray-700">No dormant users found</p>
                                    <p className="text-gray-400 text-xs mt-1">Try a wider date range or uncheck "Still Inactive Only".</p>
                                </div>
                                <button onClick={fetchData} className="px-5 py-2 rounded-lg bg-[#0A2A4F] text-white font-medium text-sm hover:bg-[#0d3566] transition">
                                    Refresh
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block flex-1 overflow-auto">
                        <table className="w-full border-collapse text-[13px]" style={{ minWidth: 1200 }}>
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    <th className={TH} style={{ width: 44 }}>#</th>
                                    <th className={TH}>User Info</th>
                                    <th className={TH}>Shop Name</th>
                                    <th className={TH}>Mobile</th>
                                    <th className={TH}>Role</th>
                                    <th className={TH}>Parent ID</th>
                                    <th className={TH}>Status</th>
                                    <th className={TH}>Inactivated At</th>
                                    <th className={TH}>Last Txn At</th>
                                    <th className={`${TH} text-center`}>Days Idle</th>
                                    <th className={TH}>Reactivated At</th>
                                    <th className={TH}>Reactivated By</th>
                                    <th className={`${TH} text-center`}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                                ) : users.length > 0 ? (
                                    users.map((u, i) => (
                                        <tr key={u.userId ?? i} className="hover:bg-[#F8FAFF] transition-colors">
                                            <td className={`${TD} text-gray-400 text-center`}>{i + 1}</td>

                                            {/* User Info */}
                                            <td className={TD}>
                                                <div className="font-semibold text-[#0A2A4F] leading-snug">{u.fullName ?? u.username ?? "-"}</div>
                                                <div className="text-[11px] text-gray-500 font-mono font-bold mt-0.5">{u.bpUserId ?? "-"}</div>
                                                <div className="text-[11px] text-gray-400 mt-0.5">{u.username ?? ""}</div>
                                            </td>

                                            <td className={TD}>
                                                <div className="font-medium text-gray-800">{u.shopName ?? "-"}</div>
                                            </td>

                                            <td className={TD}>{u.mobileNumber ?? "-"}</td>
                                            <td className={TD}>
                                                <span className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-700 rounded text-[11px] font-semibold">
                                                    {u.role ?? "-"}
                                                </span>
                                            </td>
                                            <td className={`${TD} font-mono text-[11px] text-gray-500`}>{u.parentId ?? "-"}</td>

                                            <td className={TD}><StatusBadge status={u.status} /></td>

                                            <td className={TD}>
                                                <span className="text-red-600 font-medium">{u.inactivatedAt ?? "-"}</span>
                                            </td>

                                            <td className={TD}>
                                                {u.lastTxnAt
                                                    ? <span className="text-gray-700">{u.lastTxnAt}</span>
                                                    : <span className="text-gray-400 italic">Never</span>
                                                }
                                            </td>

                                            <td className={`${TD} text-center`}>
                                                {u.daysSinceLastTxn != null
                                                    ? <span className={`font-bold ${u.daysSinceLastTxn > 30 ? "text-red-600" : "text-amber-600"}`}>{u.daysSinceLastTxn}d</span>
                                                    : <span className="text-gray-400">-</span>
                                                }
                                            </td>

                                            <td className={TD}>
                                                {u.reactivatedAt
                                                    ? <span className="text-green-700 font-medium">{u.reactivatedAt}</span>
                                                    : <span className="text-gray-300">—</span>
                                                }
                                            </td>

                                            <td className={TD}>
                                                {u.reactivatedBy
                                                    ? <span className="text-gray-600 font-mono text-[11px]">{u.reactivatedBy}</span>
                                                    : <span className="text-gray-300">—</span>
                                                }
                                            </td>

                                            <td className={`${TD} text-center`}>
                                                {u.status !== "ACTIVE" ? (
                                                    <button
                                                        onClick={() => setActivateModal(u)}
                                                        className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold text-[12px] transition shadow-sm"
                                                    >
                                                        Activate
                                                    </button>
                                                ) : (
                                                    <span className="text-[12px] text-green-600 font-semibold">Active</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={13} className="border-0 py-24 text-center">
                                            <div className="flex flex-col items-center gap-4 px-6">
                                                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">😴</div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700">No dormant users found</p>
                                                    <p className="text-gray-400 text-sm mt-1">Try a wider date range or uncheck "Still Inactive Only".</p>
                                                </div>
                                                <button onClick={fetchData}
                                                    className="px-5 py-2 rounded-lg bg-[#0A2A4F] text-white font-medium text-sm hover:bg-[#0d3566] transition shadow-sm">
                                                    Refresh
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ══ MODALS ══ */}
            {activateModal && (
                <ActivateModal
                    user={activateModal}
                    onConfirm={handleActivate}
                    onClose={() => { if (!activateLoading) setActivateModal(null); }}
                    loading={activateLoading}
                />
            )}

            {/* ══ TOAST ══ */}
            <Toast toast={toast} />
        </div>
    );
};

export default DormantUser;
